/*
 * The activity feed behind the bell (like Jobber's): a panel on the right with what happened across the company, newest
 * first. Each row opens the record it is about. Customize feed turns kinds on and off, kept for this login on this device.
 */
import { Link } from "@tanstack/react-router";
import { FEED_ACCESS, FEED_KINDS, FEED_LABELS, type FeedItem, type FeedKind } from "@steward/shared";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useFeed } from "../api/hooks";
import { useSession } from "../app/session";
import { Icon, type IconName } from "./Icon";
import { ago, Button, Skeleton, Switch } from "./parts";

const ICONS: Record<FeedKind, IconName> = {
  clients: "clients",
  requests: "requests",
  quotes: "quotes",
  jobs: "jobs",
  visits: "check",
  invoices: "invoices",
  payments: "money",
  notes: "note",
  marketing: "marketing",
  timesheets: "clock",
};

/** The kinds this login turned off, per company. Missing or unreadable storage means all on. */
function useHidden(key: string) {
  const read = (): FeedKind[] => {
    try {
      const raw = localStorage.getItem(key);
      const list: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((k): k is FeedKind => FEED_KINDS.includes(k as FeedKind)) : [];
    } catch {
      return [];
    }
  };
  const [hidden, setHidden] = useState(read);
  const save = (next: FeedKind[]) => {
    setHidden(next);
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // Not saved; the choice lasts until the page closes.
    }
  };
  return [hidden, save] as const;
}

export function ActivityFeed({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { can, me, company } = useSession();
  const [customize, setCustomize] = useState(false);
  const [hidden, setHidden] = useHidden(`steward.feed.${me.id}.${company.id}`);
  const allowed = FEED_KINDS.filter((k) => can(FEED_ACCESS[k]));
  const kinds = allowed.filter((k) => !hidden.includes(k));
  const feed = useFeed(kinds, open);

  useEffect(() => {
    if (!open) {
      setCustomize(false);
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const items = feed.data?.pages.flatMap((p) => p.items) ?? [];

  return createPortal(
    <>
      <div className="feed-scrim" onClick={onClose} />
      <aside className="feed" role="dialog" aria-label="Activity feed">
        <div className="feed-head">
          <h2>Activity feed</h2>
          <button type="button" className="iconbtn" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>
        <div className="feed-bar">
          {customize ? (
            <Button type="secondary" size="small" onClick={() => setCustomize(false)}>
              Back
            </Button>
          ) : (
            <Button type="secondary" size="small" onClick={() => setCustomize(true)}>
              Customize feed
            </Button>
          )}
        </div>
        <div className="feed-body">
          {customize ? (
            allowed.map((k) => (
              <div key={k} className="feed-toggle">
                <span>{FEED_LABELS[k]}</span>
                <Switch
                  label={`Show ${FEED_LABELS[k].toLowerCase()}`}
                  checked={!hidden.includes(k)}
                  onChange={(on) => setHidden(on ? hidden.filter((h) => h !== k) : [...hidden, k])}
                />
              </div>
            ))
          ) : !kinds.length ? (
            <p className="feed-empty muted">Everything is turned off. Tap Customize feed to pick what shows.</p>
          ) : feed.isPending ? (
            <div className="feed-empty">
              <Skeleton rows={6} />
            </div>
          ) : feed.isError ? (
            <p className="feed-empty muted">
              The feed did not load.{" "}
              <Button type="tertiary" size="small" onClick={() => void feed.refetch()}>
                Try again
              </Button>
            </p>
          ) : !items.length ? (
            <p className="feed-empty muted">Nothing yet. New clients, quotes, jobs and payments show up here.</p>
          ) : (
            <>
              {items.map((it) => (
                <FeedRow key={it.id} item={it} onOpen={onClose} />
              ))}
              {feed.hasNextPage ? (
                <div className="feed-more">
                  <Button
                    type="secondary"
                    size="small"
                    loading={feed.isFetchingNextPage}
                    onClick={() => void feed.fetchNextPage()}
                  >
                    Show more
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </aside>
    </>,
    document.body,
  );
}

function FeedRow({ item, onOpen }: { item: FeedItem; onOpen: () => void }) {
  const body = (
    <>
      <span className={`feed-icon feed-${item.kind}`}>
        <Icon name={ICONS[item.kind]} size={20} />
      </span>
      <span className="feed-text">
        <b>{item.title}</b>
        {item.quote ? <span className="feed-quote">{item.quote}</span> : null}
        {item.detail || item.client ? (
          <i>
            {item.detail}
            {item.detail && item.client ? <br /> : null}
            {item.client}
          </i>
        ) : null}
        <span className="muted small">{ago(item.at)}</span>
      </span>
    </>
  );
  return item.link ? (
    <Link to={item.link} className="feed-row" onClick={onOpen}>
      {body}
    </Link>
  ) : (
    <div className="feed-row">{body}</div>
  );
}
